import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserPlus, Check, X, Clock, ArrowRight, Archive, RotateCcw, Ban, Trash2, Link as LinkIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Avatar, EmptyState, Loader } from "@/components/common";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput } from "@/components/Modal";

const TABS = [
  { id: "active", label: "Active" },
  { id: "requests", label: "Requests" },
  { id: "archived", label: "Archived" },
];

export default function ConnectionsPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("active");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [invBusy, setInvBusy] = useState(false);

  const createInvite = async () => {
    setInvBusy(true);
    try {
      const { data } = await api.post("/connections/invite", {});
      setInviteLink(data.link);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setInvBusy(false);
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Link copied!");
    } catch {
      toast.message(inviteLink);
    }
  };

  const load = async () => {
    const { data } = await api.get("/connections");
    setData(data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const act = async (fn) => {
    try {
      await fn();
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const sendRequest = async () => {
    if (!identifier.trim()) return;
    setBusy(true);
    try {
      await api.post("/connections/request", { identifier });
      toast.success("Request sent!");
      setAddOpen(false);
      setIdentifier("");
      setTab("requests");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  const incoming = data.incoming || [];
  const outgoing = data.outgoing || [];
  const reqCount = incoming.length + outgoing.length;

  return (
    <div className="iks-fade-up">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center">
            <Users size={20} className="text-sky-400" />
          </div>
          <h1 className="font-display font-bold text-2xl text-slate-50">Connections</h1>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-connection-btn"><UserPlus size={16} /> Add Person</Button>
      </div>

      <div className="flex gap-1 p-1 bg-slate-900/60 rounded-xl mb-5 w-full sm:w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`conn-tab-${t.id}`}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {t.label}
            {t.id === "requests" && reqCount > 0 && (
              <span className="ml-1.5 text-xs bg-sky-400 text-[#070A12] rounded-full px-1.5">{reqCount}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "active" && (
        data.active.length === 0 ? (
          <EmptyState icon={Users} title="No connections yet" subtitle="Connect with someone to create a private shared space just for the two of you." action={<Button onClick={() => setAddOpen(true)}><UserPlus size={16} /> Add Person</Button>} testId="active-empty" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.active.map((c) => (
              <button key={c.id} onClick={() => nav(`/connections/${c.id}`)} data-testid="connection-card" className="iks-card p-4 flex items-center gap-3 text-left hover:border-sky-500/40 transition-all hover:-translate-y-0.5">
                <Avatar photo={c.person.profile_photo} name={c.person.name} size={48} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-100 truncate">{c.person.name}</p>
                  <p className="text-slate-500 text-sm truncate">@{c.person.username}</p>
                </div>
                <ArrowRight size={18} className="text-slate-600" />
              </button>
            ))}
          </div>
        )
      )}

      {tab === "requests" && (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-2">Incoming</p>
            {incoming.length === 0 ? (
              <p className="text-slate-500 text-sm iks-card p-4">No incoming requests.</p>
            ) : incoming.map((c) => (
              <div key={c.id} className="iks-card p-4 flex items-center gap-3 mb-2" data-testid="incoming-request">
                <Avatar photo={c.person.profile_photo} name={c.person.name} size={44} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-100 truncate">{c.person.name}</p>
                  <p className="text-slate-500 text-sm">wants to connect</p>
                </div>
                <Button variant="secondary" className="!py-2 !px-3" onClick={() => act(() => api.post(`/connections/${c.id}/decline`))} data-testid="decline-btn"><X size={16} /></Button>
                <Button className="!py-2 !px-3" onClick={() => act(() => api.post(`/connections/${c.id}/accept`))} data-testid="accept-btn"><Check size={16} /></Button>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-2">Sent</p>
            {outgoing.length === 0 ? (
              <p className="text-slate-500 text-sm iks-card p-4">No pending sent requests.</p>
            ) : outgoing.map((c) => (
              <div key={c.id} className="iks-card p-4 flex items-center gap-3 mb-2">
                <Avatar photo={c.person.profile_photo} name={c.person.name} size={44} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-100 truncate">{c.person.name}</p>
                  <p className="text-slate-500 text-sm flex items-center gap-1"><Clock size={12} /> Pending</p>
                </div>
                <Button variant="secondary" className="!py-2" onClick={() => act(() => api.post(`/connections/${c.id}/cancel`))}>Cancel</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "archived" && (
        (data.archived.length === 0 && data.blocked.length === 0) ? (
          <EmptyState icon={Archive} title="Nothing archived" subtitle="Removed connections are kept here and can be restored." />
        ) : (
          <div className="space-y-2">
            {data.archived.map((c) => (
              <div key={c.id} className="iks-card p-4 flex items-center gap-3" data-testid="archived-card">
                <Avatar photo={c.person.profile_photo} name={c.person.name} size={44} ring={false} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-300 truncate">{c.person.name}</p>
                  <p className="text-slate-500 text-sm">Archived</p>
                </div>
                <Button variant="secondary" className="!py-2 !px-3" onClick={() => act(() => api.post(`/connections/${c.id}/restore`))} data-testid="restore-btn"><RotateCcw size={15} /></Button>
                <Button variant="destructive" className="!py-2 !px-3" onClick={() => { if (confirm("Permanently delete this connection and all its shared data? This cannot be undone.")) act(() => api.delete(`/connections/${c.id}`)); }} data-testid="perma-delete-btn"><Trash2 size={15} /></Button>
              </div>
            ))}
            {data.blocked.map((c) => (
              <div key={c.id} className="iks-card p-4 flex items-center gap-3">
                <Avatar photo={c.person.profile_photo} name={c.person.name} size={44} ring={false} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-300 truncate">{c.person.name}</p>
                  <p className="text-red-400/80 text-sm flex items-center gap-1"><Ban size={12} /> Blocked</p>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Modal
        open={addOpen} onClose={() => setAddOpen(false)} title="Add a person" testId="add-connection-modal"
        footer={
          <>
            <Button variant="secondary" full onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button full loading={busy} onClick={sendRequest} data-testid="send-request-btn">Send request</Button>
          </>
        }
      >
        <p className="text-slate-400 text-sm mb-4">Enter their username, email, or phone. They'll need to accept before your shared space is created.</p>
        <Field label="Username, email, or phone">
          <TextInput value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="wifey or wifey@email.com" data-testid="connection-identifier" autoFocus />
        </Field>

        <div className="relative my-5 text-center">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
          <span className="relative bg-[#111827] px-3 text-xs text-slate-500 uppercase tracking-wider">or</span>
        </div>
        <p className="text-slate-400 text-sm mb-2">Share an invite link with anyone — even if they don't have an account yet.</p>
        {inviteLink ? (
          <div className="flex gap-2">
            <TextInput readOnly value={inviteLink} className="text-xs" data-testid="invite-link-input" />
            <Button variant="secondary" onClick={copyInvite} data-testid="copy-invite-btn"><Copy size={15} /></Button>
          </div>
        ) : (
          <Button variant="secondary" full loading={invBusy} onClick={createInvite} data-testid="create-invite-btn"><LinkIcon size={15} /> Create invite link</Button>
        )}
      </Modal>
    </div>
  );
}
